import React, { useEffect, useState } from "react";
import api from "../../services/api";

interface Customer {
  id: string;
  phone: string;
  first_name: string;
  last_name: string;
  points_balance: number;
  lifetime_spend: number;
  last_visit: string | null;
}

interface LoyaltyLookupProps {
  onCustomerSelected: (customer: Customer) => void;
  onClose: () => void;
  currentCustomer: Customer | null;
  onRemoveCustomer: () => void;
  onRedeemPoints: (customerId: string) => void;
  redemptionThreshold: number;
  redemptionValue: number;
}

export default function LoyaltyLookup({
  onCustomerSelected, onClose, currentCustomer, onRemoveCustomer, onRedeemPoints,
  redemptionThreshold, redemptionValue,
}: LoyaltyLookupProps) {
  const [phone, setPhone] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState<Customer[]>([]);
  const [notFound, setNotFound] = useState(false);
  const [showSignup, setShowSignup] = useState(false);
  const [signupName, setSignupName] = useState("");

  const formatPhone = (value: string): string => {
    const digits = value.replace(/\D/g, "");
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  // One box for everything staff used in mPower: a phone number, a name, or a
  // scanned card (15-17 digits in one burst). Results appear as they type.
  const handlePhoneChange = (value: string) => {
    if (/[a-z]/i.test(value)) {
      setPhone(value);
    } else {
      const digits = value.replace(/\D/g, "");
      if (digits.length > 17) return;
      setPhone(digits.length > 10 ? digits : formatPhone(digits));
    }
    setNotFound(false);
  };

  const isPhoneLike = !/[a-z]/i.test(phone);
  const searchable = isPhoneLike ? phone.replace(/\D/g, "").length >= 3 : phone.trim().length >= 2;

  const lookup = async (q: string = phone) => {
    setSearching(true);
    try {
      const res = await api.get("/loyalty/customers/search", { params: { q } });
      const rows: Customer[] = res.data || [];
      setResults(rows);
      setNotFound(rows.length === 0);
    } catch {
      setResults([]);
      setNotFound(true);
    } finally {
      setSearching(false);
    }
  };

  useEffect(() => {
    if (!searchable) { setResults([]); setNotFound(false); return; }
    const t = setTimeout(() => lookup(phone), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phone]);

  const selectCustomer = (customer: Customer) => {
    onCustomerSelected(customer);
  };

  const quickSignup = async () => {
    if (!signupName.trim()) return;
    const digits = phone.replace(/\D/g, "");
    try {
      const names = signupName.trim().split(" ");
      const res = await api.post("/loyalty/customers", {
        phone: digits,
        first_name: names[0],
        last_name: names.slice(1).join(" ") || null,
        marketing_opt_in_sms: true,
      });
      onCustomerSelected(res.data);
    } catch (err) {
      console.error("Signup failed:", err);
    }
  };

  const canRedeem = currentCustomer && Number(currentCustomer.points_balance) >= redemptionThreshold;

  // If customer is already attached, show their info
  if (currentCustomer) {
    return (
      <div className="fixed inset-0 flex items-center justify-center z-50" style={{ background: "rgba(51,63,72,0.85)" }}>
        <div className="w-full max-w-sm rounded-2xl overflow-hidden" style={{ background: "var(--rbw-bg-card)", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
          <div className="px-6 py-4" style={{ background: "var(--rbw-navy)" }}>
            <h2 className="text-lg font-bold text-white">Loyalty Member</h2>
          </div>

          <div className="px-6 py-5">
            <div className="text-center mb-4">
              <div
                className="w-14 h-14 rounded-full mx-auto flex items-center justify-center text-xl font-bold mb-2"
                style={{ background: "var(--rbw-gold)", color: "var(--rbw-navy)" }}
              >
                {currentCustomer.first_name?.[0]}{currentCustomer.last_name?.[0] || ""}
              </div>
              <div className="text-lg font-bold" style={{ color: "var(--rbw-navy)" }}>
                {currentCustomer.first_name} {currentCustomer.last_name}
              </div>
              <div className="text-sm" style={{ color: "var(--rbw-gray-dark)" }}>
                {currentCustomer.phone}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="rounded-xl p-3 text-center" style={{ background: "var(--rbw-bg)" }}>
                <div className="text-2xl font-bold" style={{ color: "var(--rbw-navy)" }}>
                  {currentCustomer.points_balance}
                </div>
                <div className="text-xs" style={{ color: "var(--rbw-gray-dark)" }}>Points</div>
              </div>
              <div className="rounded-xl p-3 text-center" style={{ background: "var(--rbw-bg)" }}>
                <div className="text-2xl font-bold" style={{ color: "var(--rbw-navy)" }}>
                  ${Number(currentCustomer.lifetime_spend).toFixed(0)}
                </div>
                <div className="text-xs" style={{ color: "var(--rbw-gray-dark)" }}>Lifetime</div>
              </div>
            </div>

            {canRedeem && (
              <button
                onClick={() => onRedeemPoints(currentCustomer.id)}
                className="w-full py-3 rounded-xl font-bold mb-3 active:scale-[0.98]"
                style={{ background: "#2D8A4E15", color: "var(--rbw-success)", border: "2px solid var(--rbw-success)" }}
              >
                Redeem {redemptionThreshold} pts for ${redemptionValue.toFixed(2)} Off
              </button>
            )}

            <div className="flex gap-2">
              <button
                onClick={onRemoveCustomer}
                className="flex-1 py-2.5 rounded-xl font-medium text-sm"
                style={{ background: "var(--rbw-gray-light)", color: "var(--rbw-navy)" }}
              >
                Remove
              </button>
              <button
                onClick={onClose}
                className="flex-1 py-2.5 rounded-xl font-bold text-sm"
                style={{ background: "var(--rbw-navy)", color: "#fff" }}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50" style={{ background: "rgba(51,63,72,0.85)" }}>
      <div className="w-full max-w-sm rounded-2xl overflow-hidden" style={{ background: "var(--rbw-bg-card)", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
        <div className="px-6 py-4 flex items-center justify-between" style={{ background: "var(--rbw-navy)" }}>
          <h2 className="text-lg font-bold text-white">Loyalty Lookup</h2>
          <button onClick={onClose} className="text-2xl leading-none" style={{ color: "var(--rbw-gray)" }}>&times;</button>
        </div>

        <div className="px-6 py-5">
          {!showSignup ? (
            <>
              {/* Phone number, name, or a scanned loyalty card */}
              <div className="mb-4">
                <label className="block text-sm font-semibold mb-2" style={{ color: "var(--rbw-navy)" }}>
                  Phone, name, or scan card
                </label>
                <input
                  type="text"
                  value={phone}
                  onChange={(e) => handlePhoneChange(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key !== "Enter") return;
                    if (results.length === 1) selectCustomer(results[0]);
                    else if (searchable) lookup();
                  }}
                  placeholder="(555) 555-5555 or Smith"
                  className="w-full px-4 py-3 rounded-xl text-lg text-center outline-none mb-1"
                  style={{ border: "2px solid var(--rbw-border)", color: "var(--rbw-navy)" }}
                  onFocus={(e) => (e.target.style.borderColor = "var(--rbw-blue)")}
                  onBlur={(e) => (e.target.style.borderColor = "var(--rbw-border)")}
                  autoFocus
                />
                <div className="text-xs text-center" style={{ color: "var(--rbw-gray-dark)", minHeight: 16 }}>
                  {searching ? "Searching..." : results.length > 1 ? `${results.length} matches - tap one` : ""}
                </div>
              </div>

              {/* Matches */}
              <div style={{ maxHeight: 320, overflowY: "auto" }}>
                {results.map((c) => (
                  <div
                    key={c.id}
                    className="rounded-xl p-3 mb-2 cursor-pointer transition-all active:scale-[0.98]"
                    style={{ background: "#2D8A4E10", border: "2px solid #2D8A4E30" }}
                    onClick={() => selectCustomer(c)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-bold" style={{ color: "var(--rbw-navy)" }}>
                          {[c.first_name, c.last_name].filter(Boolean).join(" ") || "(no name)"}
                        </div>
                        <div className="text-sm" style={{ color: "var(--rbw-gray-dark)" }}>
                          {c.phone ? formatPhone(c.phone) : "no phone"}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold" style={{ color: "var(--rbw-navy)" }}>
                          {c.points_balance} pts
                        </div>
                        <div className="text-xs" style={{ color: "var(--rbw-success)" }}>Tap to select</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Not found */}
              {notFound && (
                <div className="text-center py-4">
                  <div className="text-sm mb-3" style={{ color: "var(--rbw-gray-dark)" }}>
                    No member found
                  </div>
                  {isPhoneLike && phone.replace(/\D/g, "").length >= 10 && <button
                    onClick={() => setShowSignup(true)}
                    className="px-5 py-2.5 rounded-xl font-semibold"
                    style={{ background: "var(--rbw-gold)", color: "var(--rbw-navy)" }}
                  >
                    Quick Sign Up
                  </button>}
                </div>
              )}
            </>
          ) : (
            <>
              {/* Quick signup */}
              <div className="text-center mb-4">
                <div className="text-sm" style={{ color: "var(--rbw-gray-dark)" }}>
                  Signing up: {phone}
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-semibold mb-2" style={{ color: "var(--rbw-navy)" }}>
                  Customer Name
                </label>
                <input
                  value={signupName}
                  onChange={(e) => setSignupName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && quickSignup()}
                  placeholder="First Last"
                  className="w-full px-4 py-3 rounded-xl text-lg outline-none"
                  style={{ border: "2px solid var(--rbw-border)", color: "var(--rbw-navy)" }}
                  onFocus={(e) => (e.target.style.borderColor = "var(--rbw-blue)")}
                  onBlur={(e) => (e.target.style.borderColor = "var(--rbw-border)")}
                  autoFocus
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowSignup(false)}
                  className="flex-1 py-3 rounded-xl font-medium"
                  style={{ background: "var(--rbw-gray-light)", color: "var(--rbw-navy)" }}
                >
                  Back
                </button>
                <button
                  onClick={quickSignup}
                  disabled={!signupName.trim()}
                  className="flex-1 py-3 rounded-xl font-bold disabled:opacity-40"
                  style={{ background: "var(--rbw-gold)", color: "var(--rbw-navy)" }}
                >
                  Sign Up
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
